import type {
  BatchArgs,
  BatchResponse,
  DemoOverlay,
  Folder,
  IDemo,
  IEditorDraft,
  IRecording,
  IVariable,
  RecordingTypes,
  Workspace,
} from '@carboncopyco/types';
import { type DemoShare, ShareSourceType } from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import type { EndpointDefinitions } from '@reduxjs/toolkit/dist/query/react';
import type { QueryKeys } from '@reduxjs/toolkit/src/query/core/apiState';
import { folderApi, workspaceApi } from '..';
import type { GetSearchLinkResultsArgs } from '../../types';
import { handleBatchRequest } from '../utils';
import { resetInfiniteScrollQuery } from '../utils/infiniteScroll';
import {
  queryDataUpdateHandler,
  workspaceAddAsset,
  workspaceRemoveAsset,
} from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';
import {
  type CreatePresentationArgs,
  presentationApi,
} from './presentationApi';
import { type CreateShareArgs, shareApi } from './shareApi';

Debug.extend('demostack-data:api:demoApi');

export interface GetDemoArgs {
  workspaceId?: string;
  organizationId: string;
  id: string;
  removeDeleted?: boolean;
}

export interface GetDemosByRecordingArgs {
  organizationId: string;
  id: string;
  removeDeleted?: boolean;
  recordingId: number;
}

export interface CreateDemoArgs {
  organizationId: string;
  recordingId: IRecording['id'];
  demoName: string;
  description?: string;
  image?: string;
  isAvailable?: boolean;
  folderId?: Folder['id'];
}

export interface RevertDemoArgs {
  organizationId: string;
  id: string;
  revisionId: string;
}

export interface RestoreDemoArgs {
  organizationId: string;
  id: string;
}

export interface UpdateDemoFromRecordingArgs {
  organizationId: string;
  id: string;
  revisionId?: string;
}

export interface DuplicateDemoArgs {
  organizationId: string;
  id: IDemo['id'];
  revisionId?: string;
  folderId?: Folder['id'];
}

export interface CopyDemoArgs {
  organizationId: string;
  id: IDemo['id'];
  demoName?: IDemo['demoName'];
  targetWorkspaceId: Workspace['id'];
  folderId?: Folder['id'];
}

export interface UpdateDemoArgs {
  organizationId: string;
  workspaceId?: string;
  id: string;
  demoName?: string;
  description?: string;
  firstUrl?: string;
  image?: string;
  isAvailable?: boolean;
  isSharingApproved?: boolean;
}

export interface DeleteDemoArgs {
  organizationId: string;
  id: string;
  folderId?: Folder['id'];
}

export interface CreateDemoDraftArgs {
  organizationId: string;
  id: string;
  draftName?: string;
  variables?: IVariable[];
}

export type CreateDemoDraftResponse = IEditorDraft;

export interface GetDemosArgs extends BatchArgs {
  organizationId: string;
  workspaces?: string;
  recordingType?: RecordingTypes | RecordingTypes[];
}

export interface CreateDemoOverlayArgs {
  organizationId: string | number;
  firstUrl: string;
  workspaceId?: Workspace['id'];
  demoName?: string;
  folderId?: Folder['id'];
}

export interface GetDemoAnalyticsArgs extends BatchArgs {
  organizationId: string;
  id: string;
  shareIds?: string[];
  presentationIds?: string[];
  activityType?: 'visits' | 'presented';
}

export interface MoveDemoArgs {
  organizationId: string;
  id: IDemo['id'];
  targetFolderId?: Folder['id'];
  currentFolderId?: Folder['id'];
  workspaceId?: Workspace['id'];
}

export const demoApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getDemo: builder.query<IDemo, GetDemoArgs>({
      query: ({ organizationId, workspaceId, id, removeDeleted = false }) => ({
        url: `/api/${organizationId}/demo/${id}`,
        method: 'GET',
        params: removeUndefinedValues({
          workspaceId,
          removeDeleted: String(removeDeleted),
        }),
      }),
      providesTags: (_, __, { id }) => [{ type: 'Demo', id }],
    }),

    getDemos: builder.query<BatchResponse<IDemo>, GetDemosArgs>({
      query: ({ organizationId, workspaces = 'all', recordingType, ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({
            workspaces,
            recordingType,
          }),
          url: `/api/${organizationId}/demos`,
        }),
      providesTags: ['Demos'],
    }),

    getArchivedDemos: builder.query<BatchResponse<IDemo>, GetDemosArgs>({
      query: ({ organizationId, workspaces = 'all', ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ workspaces }),
          url: `/api/${organizationId}/demos/archived`,
        }),
      providesTags: ['ArchivedDemos'],
    }),

    getDemosByRecording: builder.query<
      BatchResponse<IDemo>,
      GetDemosByRecordingArgs
    >({
      query: ({ organizationId, recordingId, removeDeleted, ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ removeDeleted }),
          url: `/api/${organizationId}/${recordingId}/demos`,
        }),
      providesTags: (_, __, { recordingId }) => [
        { type: 'RecordingDemos', id: recordingId },
      ],
    }),

    createDemo: builder.mutation<IDemo, CreateDemoArgs>({
      query: ({
        organizationId,
        demoName,
        description,
        image,
        isAvailable,
        recordingId,
        folderId,
      }) => ({
        url: `/api/${organizationId}/demos/new`,
        method: 'PUT',
        body: removeUndefinedValues({
          demoName,
          description,
          image,
          isAvailable,
          recordId: recordingId,
          folderId,
        }),
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'demo',
        });
      },
      invalidatesTags: (result, __, { recordingId }) =>
        result
          ? [
              { type: 'Demos' },
              { type: 'RecordingDemos', id: recordingId },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
              { type: 'FolderAssets', id: result.folderId },
            ]
          : [{ type: 'Demos' }, { type: 'RecordingDemos', id: recordingId }],
    }),

    updateDemo: builder.mutation<IDemo, UpdateDemoArgs>({
      query: ({
        organizationId,
        id,
        demoName,
        description,
        firstUrl,
        image,
        isAvailable,
        isSharingApproved,
      }) => ({
        url: `/api/${organizationId}/demo/${id}`,
        method: 'POST',
        body: {
          demoName,
          description,
          firstUrl,
          image,
          isAvailable,
          isSharingApproved,
        },
      }),
      onQueryStarted: async (
        { organizationId, workspaceId, id, ...patch },
        { dispatch, queryFulfilled, getState }
      ) => {
        const state = getState();
        const patchResult = dispatch(
          demoApi.util.updateQueryData(
            'getDemo',
            { organizationId, workspaceId, id },
            (draft) => Object.assign(draft, patch)
          )
        );
        try {
          await queryFulfilled;
          resetInfiniteScrollQuery({
            state,
            dispatch,
            api: workspaceApi,
            endpoint: 'getWorkspaceAssets' as QueryKeys<EndpointDefinitions>,
            tagsToInvalidate: [{ type: 'WorkspaceAssets' }],
          });
        } catch {
          patchResult.undo();
        }
        queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: (result, _, { id }) =>
        result
          ? [
              { type: 'Demo', id },
              { type: 'Demos' },
              {
                type: 'RecordingDemos',
                id: result.recordId,
              },
              { type: 'Revisions', id },
              { type: 'FolderAssets', id: result.folderId },
              { type: 'Playbook' },
            ]
          : [{ type: 'Demo', id }, { type: 'Demos' }],
    }),

    duplicateDemo: builder.mutation<IDemo, DuplicateDemoArgs>({
      query: ({ organizationId, id, revisionId, folderId }) => ({
        url: `/api/${organizationId}/demo/${id}/duplicate`,
        method: 'PUT',
        body: { revisionId, folderId },
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'demo',
        });
      },
      invalidatesTags: (res, __, { folderId }) =>
        res
          ? [
              { type: 'Demos' },
              { type: 'RecordingDemos' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
            ]
          : [
              { type: 'Demos' },
              { type: 'RecordingDemos' },
              { type: 'FolderAssets', id: folderId },
            ],
    }),

    copyDemo: builder.mutation<IDemo, CopyDemoArgs>({
      query: ({
        organizationId,
        id,
        demoName,
        targetWorkspaceId,
        folderId,
      }) => ({
        url: `/api/${organizationId}/demo/${id}/copy`,
        method: 'PUT',
        body: removeUndefinedValues({
          folderId,
          demoName,
          targetWorkspaceId,
        }),
      }),
      invalidatesTags: (_, __, { targetWorkspaceId, folderId }) => [
        { type: 'Demos' },
        { type: 'RecordingDemos' },
        { type: 'WorkspaceAssets', id: targetWorkspaceId },
        { type: 'FolderAssets', id: folderId },
        { type: 'Folders', id: targetWorkspaceId },
      ],
    }),

    moveDemo: builder.mutation<IDemo, MoveDemoArgs>({
      query: ({ organizationId, id, targetFolderId }) => ({
        url: `/api/${organizationId}/demo/${id}/move`,
        method: 'PUT',
        body: removeUndefinedValues({
          folderId: targetFolderId,
        }),
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const isMovingFromWorkspaceToFolder =
            arg.targetFolderId !== '0' && arg.currentFolderId === '0';
          await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            api: isMovingFromWorkspaceToFolder ? workspaceApi : folderApi,
            state,
            dispatch,
            endpointName: isMovingFromWorkspaceToFolder
              ? ('getWorkspaceAssets' as QueryKeys<EndpointDefinitions>)
              : ('getFolderAssets' as QueryKeys<EndpointDefinitions>),
            endpointArgumentKey: isMovingFromWorkspaceToFolder
              ? 'workspaceId'
              : 'folderId',
            endpointArgumentValue: isMovingFromWorkspaceToFolder
              ? arg.workspaceId
              : arg.currentFolderId,
            updateQueryDataFunction: (assets) =>
              workspaceRemoveAsset(assets, arg),
          });
        } catch (error) {
          console.error(error);
        }
      },
      invalidatesTags: (res, __, { currentFolderId, id, targetFolderId }) => [
        { type: 'Demo', id },
        { type: 'WorkspaceAssets', id: res?.workspaceId },
        { type: 'FolderAssets', id: currentFolderId },
        { type: 'FolderAssets', id: targetFolderId },
        { type: 'Folders', id: res?.workspaceId },
      ],
    }),

    updateDemoFromRecording: builder.mutation<
      IDemo,
      UpdateDemoFromRecordingArgs
    >({
      query: ({ organizationId, id, revisionId }) => ({
        url: `/api/${organizationId}/demo/${id}/recording-update`,
        method: 'POST',
        body: { revisionId },
      }),
      invalidatesTags: (result, _, arg) =>
        result
          ? [
              { type: 'Demo', id: arg.id },
              { type: 'Demos' },
              { type: 'Revisions', id: arg.id },
              {
                type: 'RecordingDemos',
                id: result.recordId,
              },
              { type: 'Recording', id: result.recordId },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
              { type: 'Playbook' },
            ]
          : [
              { type: 'Demo', id: arg.id },
              { type: 'Demos' },
              { type: 'Revisions', id: arg.id },
            ],
    }),

    deleteDemo: builder.mutation<IDemo, DeleteDemoArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/demo/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (
        { folderId = '0', ...arg },
        { dispatch, getState, queryFulfilled }
      ) => {
        try {
          const { data: deletedDemo } = await queryFulfilled;
          const isDeletingFromFolder = String(folderId) !== '0';
          const state = getState();
          queryDataUpdateHandler({
            api: isDeletingFromFolder ? folderApi : workspaceApi,
            state,
            dispatch,
            endpointName: isDeletingFromFolder
              ? ('getFolderAssets' as QueryKeys<EndpointDefinitions>)
              : ('getWorkspaceAssets' as QueryKeys<EndpointDefinitions>),
            endpointArgumentKey: isDeletingFromFolder
              ? 'folderId'
              : 'workspaceId',
            endpointArgumentValue: isDeletingFromFolder
              ? folderId
              : deletedDemo.workspaceId,
            updateQueryDataFunction: (assets) => {
              return workspaceRemoveAsset(assets, arg);
            },
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (result, _, arg) =>
        result
          ? [
              { type: 'Demo', id: arg.id },
              { type: 'Demos' },
              { type: 'ArchivedDemos' },
              {
                type: 'RecordingDemos',
                id: result.recordId,
              },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
              { type: 'FolderAssets', id: arg.folderId },
              { type: 'Playbook' },
            ]
          : [
              { type: 'Demo', id: arg.id },
              { type: 'Demos' },
              { type: 'ArchivedDemos' },
            ],
    }),

    restoreDemo: builder.mutation<IDemo, RestoreDemoArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/demo/${id}/restore`,
        method: 'POST',
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: restoredDemo } = await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            state,
            dispatch,
            endpointName:
              'getWorkspaceAssets' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'workspaceId',
            endpointArgumentValue: restoredDemo.workspaceId,
            updateQueryDataFunction: (workspaceAssets) =>
              workspaceRemoveAsset(workspaceAssets, arg),
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (result, _, arg) =>
        result
          ? [
              { type: 'Demo', id: arg.id },
              { type: 'ArchivedDemos' },
              { type: 'Demos' },
              {
                type: 'RecordingDemos',
                id: result.recordId,
              },
              {
                type: 'WorkspaceAssets',
                id: result.workspaceId,
              },
            ]
          : [
              { type: 'ArchivedDemos' },
              { type: 'Demos' },
              { type: 'Demo', id: arg.id },
            ],
    }),

    revertDemo: builder.mutation<IDemo, RevertDemoArgs>({
      query: ({ organizationId, id, revisionId }) => ({
        url: `/api/${organizationId}/demo/${id}/revert`,
        method: 'PUT',
        body: { revisionId },
      }),
      invalidatesTags: (result, _, { id }) =>
        result
          ? [
              { type: 'Demos' },
              { type: 'Demo', id },
              { type: 'Revisions', id },
              {
                type: 'RecordingDemos',
                id: result.recordId,
              },
              { type: 'Drafts', id },
              { type: 'Playbook' },
            ]
          : [
              { type: 'Demos' },
              { type: 'Demo', id },
              { type: 'Revisions', id },
              { type: 'Drafts', id },
            ],
    }),

    createDemoDraft: builder.mutation<
      CreateDemoDraftResponse,
      CreateDemoDraftArgs
    >({
      queryFn: async (
        { organizationId, id, draftName, variables },
        _,
        __,
        baseQuery
      ) => {
        const newDraftResponse = await baseQuery({
          url: `/api/${organizationId}/demo/${id}/drafts/new`,
          method: 'PUT',
          body: { draftName },
        });
        if (!variables || !newDraftResponse.data) {
          return newDraftResponse;
        }
        return baseQuery({
          url: `/api/${organizationId}/draft/${
            (newDraftResponse.data as CreateDemoDraftResponse).id
          }`,
          method: 'POST',
          body: { variables },
        });
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Drafts', id },
        { type: 'Modifications' },
      ],
    }),

    resetDemo: builder.mutation<unknown, { id: string }>({
      queryFn: () => {
        return { data: null };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Demo', id },
        { type: 'Revisions', id },
      ],
    }),
    createOverlay: builder.mutation<DemoOverlay, CreateDemoOverlayArgs>({
      query: ({
        organizationId,
        firstUrl,
        workspaceId,
        demoName,
        folderId,
      }) => ({
        url: `/api/${organizationId}/demos/live/new`,
        method: 'PUT',
        body: {
          firstUrl,
          workspaceId,
          demoName,
          ...removeUndefinedValues({ folderId }),
        },
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'overlay',
        });
      },
      invalidatesTags: (_, __, { workspaceId, folderId }) => [
        { type: 'WorkspaceAssets', id: workspaceId },
        { type: 'FolderAssets', id: folderId },
        { type: 'Demos' },
      ],
    }),
    createDemoShare: builder.mutation<
      DemoShare,
      CreatePresentationArgs & Omit<CreateShareArgs, 'sourceType' | 'id'>
    >({
      queryFn: async (
        {
          organizationId,
          workspaceId,
          demoId,
          forClient,
          variables,
          summary,
          presentationName,
          linkDescription,
          skipWelcome,
          cover,
          expiresAt,
          daysToExpire,
          security,
          integrationMetadata,
          integrationLeads,
          showGuidanceTooltip,
          welcomeMatTitle,
        },
        api
      ) => {
        const createPresentationResponse = await api.dispatch(
          presentationApi.endpoints.createPresentation.initiate({
            organizationId,
            demoId,
            workspaceId,
            forClient,
            variables,
            summary,
            presentationName,
            hidden: true,
            notes: '',
            integrationMetadata,
          })
        );

        if ('data' in createPresentationResponse) {
          const { id } = createPresentationResponse.data;
          const createShareResponse = await api.dispatch(
            shareApi.endpoints.createShare.initiate({
              organizationId,
              workspaceId,
              sourceType: ShareSourceType.presentation,
              id: Number(id),
              linkDescription,
              skipWelcome,
              cover,
              expiresAt,
              daysToExpire,
              security,
              integrationLeads,
              showGuidanceTooltip,
              welcomeMatTitle,
            })
          );
          if ('data' in createShareResponse) {
            return createShareResponse as unknown as {
              data: DemoShare;
            };
          }
          return {
            ...createShareResponse,
            data: undefined,
          };
        }
        return {
          ...createPresentationResponse,
          data: undefined,
        };
      },
      invalidatesTags: [{ type: 'WorkspaceAssets' }, { type: 'Shares' }],
    }),
    getDemoSearchLinksResults: builder.query<
      BatchResponse<DemoShare>,
      GetSearchLinkResultsArgs
    >({
      query: ({
        organizationId,
        id,
        companies,
        creators,
        active,
        sources,
        workspaces,
        ...rest
      }) =>
        handleBatchRequest({
          ...rest,
          url: `/api/${organizationId}/share/demo/${id}/search`,
          method: 'POST',
          params: removeUndefinedValues({
            companies: companies?.join(','),
            creators: creators?.join(','),
            active,
            sources: sources?.join(','),
            workspaces: workspaces?.join(','),
          }),
        }),
      providesTags: [{ type: 'Shares' }],
    }),
  }),
});

export const {
  useCreateDemoShareMutation,
  useDeleteDemoMutation,
  useGetDemoQuery,
  useLazyGetDemoQuery,
  useUpdateDemoMutation,
  useDuplicateDemoMutation,
  useCopyDemoMutation,
  useUpdateDemoFromRecordingMutation,
  useRestoreDemoMutation,
  useRevertDemoMutation,
  useGetDemosQuery,
  useLazyGetDemosQuery,
  useGetArchivedDemosQuery,
  useLazyGetArchivedDemosQuery,
  useCreateDemoMutation,
  useLazyGetDemosByRecordingQuery,
  useResetDemoMutation,
  useCreateDemoDraftMutation,
  useCreateOverlayMutation,
  useGetDemoSearchLinksResultsQuery,
  useLazyGetDemoSearchLinksResultsQuery,
  useMoveDemoMutation,
} = demoApi;
